import { createFileRoute, Link } from "@tanstack/react-router";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { useMemo, useState } from "react";
import { listPosts, getPostMetaBySlug } from "@/lib/posts";

export const Route = createFileRoute("/dev/og-preview")({
  head: () => ({
    meta: [
      { title: "OG Preview (dev) | Virginia Bariatric" },
      { name: "robots", content: "noindex" },
      ...(import.meta.env.SSR ? [
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
  }),
  component: OgPreviewPage,
});

// Mirrors scripts/generate-og-images.py so the rendered text on the card
// matches what appears in the tables below.
function cleanText(s: string): string {
  return (s || "")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#8217;|&rsquo;/g, "\u2019")
    .replace(/&#8220;|&ldquo;/g, "\u201c")
    .replace(/&#8221;|&rdquo;/g, "\u201d")
    .replace(/&#8230;|&hellip;/g, "\u2026")
    .replace(/\s+/g, " ")
    .trim();
}

function truncate(s: string, max = 220): string {
  return s.length > max ? s.slice(0, max - 3).trimEnd() + "\u2026" : s;
}

function OgPreviewPage() {
  const posts = useMemo(() => listPosts(), []);
  const [slug, setSlug] = useState(posts[0]?.slug ?? "");
  const [bust, setBust] = useState(0);
  const post = getPostMetaBySlug(slug);

  const headline = post
    ? cleanText(post.seoTitle || post.title || post.slug)
    : "";
  const subtitle = post
    ? truncate(cleanText(post.seoDescription || post.excerpt || ""))
    : "";
  const cardSrc = post
    ? `/og-${post.slug}.png${bust ? `?t=${bust}` : ""}`
    : "";
  const canonical = post
    ? `https://virginiabariatric.com/dr-fitzer-dissects-the-news/${post.slug}`
    : "";

  return (
    <main className="mx-auto max-w-4xl px-6 py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">OG card preview</h1>
        <p className="text-sm text-muted-foreground">
          Dev-only. Pick a post to see the generated 1200×630 card next to the
          text the generator uses. Regenerate with{" "}
          <code className="text-xs">npm run og:generate</code>, then hit
          Reload.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <label htmlFor="slug" className="text-sm font-medium">
          Post
        </label>
        <select
          id="slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="border rounded-md px-2 py-1 text-sm bg-background"
        >
          {posts.map((p) => (
            <option key={p.slug} value={p.slug}>
              {p.title}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setBust(Date.now())}
          className="text-sm border rounded-md px-3 py-1 hover:bg-muted"
        >
          Reload image
        </button>
        {post && (
          <Link
            to="/dr-fitzer-dissects-the-news/$slug"
            params={{ slug: post.slug }}
            className="text-sm text-brand-mid hover:underline"
          >
            View live post →
          </Link>
        )}
      </div>

      {!post ? (
        <p className="text-sm text-muted-foreground">No post selected.</p>
      ) : (
        <>
          <figure className="border rounded-md overflow-hidden bg-muted">
            <img
              src={cardSrc}
              alt={`OG card for ${post.title}`}
              width={1200}
              height={630}
              className="w-full h-auto block"
            />
            <figcaption className="px-3 py-2 text-xs text-muted-foreground border-t">
              {cardSrc} · 1200×630
            </figcaption>
          </figure>

          <section className="mt-6 grid grid-cols-1 md:grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
            <div className="text-muted-foreground">Slug</div>
            <div className="font-mono break-all">{post.slug}</div>

            <div className="text-muted-foreground">Headline</div>
            <div>
              <div>{headline}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {headline.length} chars · source:{" "}
                {post.seoTitle ? "seoTitle" : "title"}
              </div>
            </div>

            <div className="text-muted-foreground">Subtitle</div>
            <div>
              <div>{subtitle || <em className="text-muted-foreground">(none)</em>}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {subtitle.length} chars · source:{" "}
                {post.seoDescription ? "seoDescription" : "excerpt"}
                {subtitle.endsWith("\u2026") ? " · truncated" : ""}
              </div>
            </div>

            <div className="text-muted-foreground">featuredImage</div>
            <div className="font-mono text-xs break-all">
              {post.featuredImage || (
                <em className="text-muted-foreground font-sans">
                  none → auto /og-{post.slug}.png
                </em>
              )}
            </div>

            <div className="text-muted-foreground">Canonical</div>
            <div className="font-mono text-xs break-all">{canonical}</div>
          </section>
        </>
      )}

      <hr className="my-8 border-border" />
      <details className="text-sm">
        <summary className="cursor-pointer text-muted-foreground">
          All posts ({posts.length})
        </summary>
        <ul className="mt-3 space-y-1">
          {posts.map((p) => (
            <li key={p.slug}>
              <button
                type="button"
                onClick={() => setSlug(p.slug)}
                className={`text-left hover:underline ${
                  p.slug === slug ? "font-semibold" : ""
                }`}
              >
                {p.title}
              </button>
            </li>
          ))}
        </ul>
      </details>
    </main>
  );
}
